/**
 * Homemade Nicotine Pouches — Interactive Safety Game.
 *
 * 5-stage console UI with Back/Next, then a result stage that paints the
 * shareable card and exposes copy / PNG-download actions.
 *
 * All factual claims here mirror the inline citations in the section file.
 * Do NOT add output that would constitute a recipe (e.g. precise grammages,
 * supplier names, extraction methods). This component teaches *why* home
 * production is hard; it does not teach how to do it.
 */

(() => {
  const LABELS = {
    nicotine_form: {
      freebase: 'Free-base concentrate',
      salt: 'Nicotine salt powder',
      nrt: 'Repurposed NRT gum',
      tobacco: 'Loose tobacco / snus',
    },
    carrier: {
      cotton: 'Cotton ball',
      tissue: 'Household tissue',
      tea: 'Loose tea leaves',
      mcc: 'Microcrystalline cellulose',
      wood_pulp: 'Pine wood-pulp fibre',
    },
    sealing: {
      open: 'No seal (twist closed)',
      glue: 'Glue / tape',
      staple: 'Staple',
      sewing: 'Sewing',
      iron: 'Household iron',
      foodsaver: 'Vacuum food-sealer bag',
      impulse: 'Impulse sealer + fleece',
    },
    sweetener: {
      none: 'None',
      sucralose: 'Sucralose',
      xylitol: 'Xylitol',
      sugar: 'Household sugar',
    },
    flavor: {
      none: 'None',
      food_flavor: 'Food-grade flavouring',
      essential_oil: 'Essential oil',
      extract: 'Kitchen extract',
    },
  };

  /** Feedback templates per step. Tone: ok | warn | bad | info */
  const FEEDBACK = {
    step1: {
      forms: {
        freebase: {
          tone: 'bad',
          title: 'High-risk starting material',
          body: 'Free-base nicotine concentrate (typically sold as a 100 mg/mL vape base) is acutely toxic on skin contact — industrial handling requires nitrile gloves and a fitted respirator, since the skin absorbs spilled concentrate rapidly. The EU TPD caps retail e-liquid at 20 mg/mL precisely because higher concentrations are easy to over-handle. <strong>Wrong carrier for the mouth:</strong> the PG/VG base is engineered for vaping; its glycerin content gives a slimy mouthfeel and slows nicotine release across the oral mucosa. <strong>Purity is another problem:</strong> vape-grade nicotine is not held to pharmaceutical heavy-metal limits (USP &lt;232&gt; / ICH Q3D), and independent testing has repeatedly found lead, nickel, and cadmium in vape products (Olmedo et al. 2018; Goniewicz et al.).',
        },
        salt: {
          tone: 'warn',
          title: 'Closer to commercial form, still not safe at home',
          body: 'Nicotine bitartrate and similar salts are what most commercial pouches use. Even the industry tolerance for labelled nicotine content (typically ±5–10 % under AEMSA / BSI guidance) needs an analytical balance with sub-milligram resolution; kitchen scales resolve to ~0.1 g, orders of magnitude too coarse for a 6 mg target. <strong>Mixing matters as much as weighing:</strong> without geometric dilution (pre-mixing the active into a small volume of carrier before folding into the rest), powder segregation can deliver 0 mg in one pouch and double the dose in the next.',
        },
        nrt: {
          tone: 'bad',
          title: 'Off-label use of an approved drug',
          body: 'NRT gum and lozenges are approved as finished drug products at a specific dose. Crushing and re-using them voids that approval, alters the release profile, and is not what their safety testing covered.',
        },
        tobacco: {
          tone: 'warn',
          title: 'This is a different product category',
          body: 'Loose tobacco creates traditional snus, not a modern nicotine pouch. Snus is regulated separately: it is banned for sale across the EU under Article 17 of Directive 2014/40/EU, with Sweden the sole derogation (since 1995); Norway and Switzerland (non-EU) also permit sales. Snus also brings tobacco-specific nitrosamines into the picture.',
        },
      },
      doseBands: [
        { max: 3, tone: 'info', text: 'Below most commercial low-strength pouches (3 mg).' },
        { max: 8, tone: 'info', text: 'Within the typical low-to-mid commercial range (3–8 mg).' },
        { max: 14, tone: 'warn', text: 'In the higher commercial band. Symptomatic over-exposure becomes more likely for non-regular users.' },
        { max: 30, tone: 'bad', text: 'Above what regulated brands typically sell. Acute nicotine intoxication risk rises sharply.' },
      ],
    },

    step2: {
      carrier: {
        cotton: {
          tone: 'bad',
          title: 'Not validated for oral mucosal contact',
          body: 'Consumer cotton is not produced to food-contact specifications. It can carry pesticide residues, bleaching by-products, and shed fibres that lodge in the gum line.',
        },
        tissue: {
          tone: 'bad',
          title: 'Bleaching residues, no microbial spec',
          body: 'Household tissue is manufactured for short skin contact, not 20–60 minutes against a wet mucosal surface. There is no documented microbial-load limit for it.',
        },
        tea: {
          tone: 'warn',
          title: 'Food-safe to drink — not the same as safe to hold against gum',
          body: 'Tea leaves contain tannins (which can irritate mucosa with prolonged contact) and fragment over time. Microbial load is controlled for brewing temperatures, not for moist, room-temperature exposure.',
        },
        mcc: {
          tone: 'warn',
          title: 'The right family of material — but specification matters',
          body: 'Microcrystalline cellulose is the carrier most commercial pouches use. The industry version is pharmaceutical-grade with documented particle size, microbial limits, and traceable lots. Consumer-grade MCC sold online does not come with those certifications.',
        },
        wood_pulp: {
          tone: 'warn',
          title: 'Same family as snus carriers — grade matters',
          body: 'Pine wood-pulp fibre is used in traditional Swedish snus and in some nicotine pouches under the GothiaTek standard. As with MCC, what is sold to consumers does not carry that grade or that testing.',
        },
      },
      sealing: {
        open: {
          tone: 'bad',
          title: 'No seal — immediate leak',
          body: 'Without any closure, saliva floods the filler in seconds and the concentrate empties directly into the mouth. Uncontrolled dosing in the worst possible way.',
        },
        glue: {
          tone: 'bad',
          title: 'Foreign chemicals in the mouth',
          body: 'Household adhesives are not approved for sustained oral contact. Solvents and plasticisers leach as saliva moistens the seal.',
        },
        staple: {
          tone: 'bad',
          title: 'Mechanical hazard',
          body: 'Sharp metal against the gum line, plus oxidation products from a wet pouch eroding the staple. Avoid.',
        },
        sewing: {
          tone: 'warn',
          title: 'Loose stitch leaks',
          body: 'Stitch holes are macroscopic — saliva-thinned filler escapes through them within minutes, even before the pouch is consumed.',
        },
        iron: {
          tone: 'warn',
          title: 'Heat that fluctuates ±20 °C',
          body: 'A household iron swings far too widely to give a clean polymer seal: you either melt the fleece through or leave gaps along the seam. Industrial impulse sealers apply a brief, calibrated heat pulse for this reason.',
        },
        foodsaver: {
          tone: 'warn',
          title: 'Wrong material entirely',
          body: 'Vacuum food-sealer bags are PE / nylon laminates engineered for cold storage. They trap rather than dispense — the filler will not release into saliva.',
        },
        impulse: {
          tone: 'ok',
          title: 'Industry-standard construction',
          body: 'An impulse sealer applies a calibrated, brief heat pulse that bonds non-woven viscose fleece without melt-through. This is the factory standard for nicotine pouch packaging.',
        },
      },
    },

    step5: {
      sweetener: {
        none: { tone: 'info', text: 'No sweetener selected.' },
        sucralose: { tone: 'ok', text: 'Sucralose is widely used in commercial pouches and has approved food-additive status (FDA, EFSA).' },
        xylitol: { tone: 'warn', text: 'Xylitol is food-safe in normal amounts but can cause laxative effects above roughly 20 g in a single dose for adults (lower for sensitive individuals and children) — not an issue at single-pouch use.' },
        sugar: { tone: 'bad', text: 'Household sugar is fermentable: it accelerates microbial growth in a wet pouch and is cariogenic during prolonged oral contact.' },
      },
      flavor: {
        none: { tone: 'info', text: 'No added flavour.' },
        food_flavor: { tone: 'ok', text: 'Food-grade flavouring compounds are what manufacturers use, dosed at <1 % typically.' },
        essential_oil: { tone: 'bad', text: 'Essential oils (peppermint, clove, eucalyptus) are not approved for sustained mucosal contact. Several are documented mucosal irritants, and wintergreen oil (methyl salicylate) is acutely toxic in gram quantities — a single teaspoon equals ~21 adult aspirin tablets in salicylate content.' },
        extract: { tone: 'warn', text: 'Kitchen extracts are typically 35–40 % alcohol; sustained mucosal exposure to alcohol at that concentration is not their tested use case.' },
      },
    },
  };

  const TONES = { ok: 'OK', warn: 'Caution', bad: 'High risk', info: 'Note' };
  const STAGES = ['1', '2', '3', '4', '5', 'result'];

  /** % free-base nicotine via Henderson-Hasselbalch (pKa ≈ 8.0). */
  const freeBasePct = (ph) => (100 / (1 + Math.pow(10, 8.0 - ph)));

  class ZpDiySimulator extends HTMLElement {
    connectedCallback() {
      this.state = {
        nicotine_form: null,
        dose_mg: 6,
        carrier: null,
        sealing: null,
        ph: 70,
        moisture_pct: 35,
        sweetener: null,
        flavor: null,
      };
      this.stageIdx = 0;
      this._scores = null;

      this.console = this.querySelector('[data-zp-diy-console]');
      this.stagesRoot = this.querySelector('[data-zp-diy-stages]');
      this.backBtn = this.querySelector('[data-zp-diy-back]');
      this.nextBtn = this.querySelector('[data-zp-diy-next]');
      this.nextLabel = this.querySelector('[data-zp-diy-next-label]');
      this.resetBtn = this.querySelector('[data-zp-diy-reset]');

      this.#bindInputs();
      this.#bindNav();
      this.#bindShareActions();
      this.#updateNav();
    }

    // ── Inputs ──────────────────────────────────────────────
    #bindInputs() {
      this.querySelectorAll('input[type="radio"]').forEach((el) => {
        el.addEventListener('change', () => {
          const field = el.closest('[data-zp-diy-field]')?.dataset.zpDiyField;
          if (!field) return;
          this.state[field] = el.value;
          this.#renderStep(field);
          this.#updateNav();
        });
      });

      this.querySelectorAll('input[type="range"][data-zp-diy-field]').forEach((el) => {
        const field = el.dataset.zpDiyField;
        el.addEventListener('input', () => {
          this.state[field] = Number(el.value);
          this.#syncDisplay(field);
          this.#renderStep(field);
        });
        this.#syncDisplay(field);
      });
    }

    #syncDisplay(field) {
      const node = this.querySelector(`[data-zp-diy-display="${field}"]`);
      if (!node) return;
      if (field === 'ph') node.textContent = (this.state.ph / 10).toFixed(1);
      else node.textContent = String(this.state[field]);

      if (field === 'ph') {
        const marker = this.querySelector('[data-zp-diy-ph-marker]');
        if (marker) {
          const phVal = this.state.ph / 10;
          const pct = ((phVal - 4) / (10.5 - 4)) * 100;
          marker.style.left = `${Math.max(0, Math.min(100, pct))}%`;
        }
      }
    }

    // ── Navigation ──────────────────────────────────────────
    #bindNav() {
      this.backBtn?.addEventListener('click', () => this.#goto(this.stageIdx - 1));
      this.nextBtn?.addEventListener('click', () => {
        if (STAGES[this.stageIdx] === '5') return this.#reveal();
        if (STAGES[this.stageIdx] === 'result') return; // disabled at result
        this.#goto(this.stageIdx + 1);
      });
      this.resetBtn?.addEventListener('click', () => this.#reset());
    }

    #goto(idx) {
      if (idx < 0 || idx >= STAGES.length) return;
      this.stageIdx = idx;
      const id = STAGES[idx];

      this.querySelectorAll('[data-stage]').forEach((el) => {
        if (!el.matches('section')) return;
        const match = el.dataset.stage === id;
        el.hidden = !match;
        el.classList.toggle('is-active', match);
      });

      this.console.dataset.stage = id;
      this.#updateNav();
      this.#updateDots();
      // Scroll the console card top into view (mobile)
      if (window.matchMedia('(max-width: 720px)').matches) {
        this.console.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    #updateDots() {
      const numNode = this.querySelector('[data-zp-diy-stage-num]');
      const isResult = STAGES[this.stageIdx] === 'result';
      const num = isResult ? 5 : this.stageIdx + 1;
      if (numNode) numNode.textContent = String(num);

      this.querySelectorAll('[data-stage-dot]').forEach((el) => {
        const dotIdx = Number(el.dataset.stageDot) - 1;
        el.classList.toggle('is-active', !isResult && dotIdx === this.stageIdx);
        el.classList.toggle('is-done',   dotIdx < this.stageIdx || isResult);
      });
    }

    #updateNav() {
      const id = STAGES[this.stageIdx];
      const required = this.#requiredForStage(id);
      const canAdvance = required.every((f) => this.state[f] != null);

      // Back: disabled at stage 1, hidden at result
      if (this.backBtn) {
        this.backBtn.hidden = id === 'result';
        this.backBtn.disabled = this.stageIdx === 0;
      }

      // Next: hidden at result; label switches on stage 5
      if (this.nextBtn) {
        this.nextBtn.hidden = id === 'result';
        this.nextBtn.disabled = !canAdvance;
        if (this.nextLabel) {
          this.nextLabel.textContent = id === '5' ? 'Reveal result' : 'Next';
        }
      }

      // Reset: only shown at result
      if (this.resetBtn) this.resetBtn.hidden = id !== 'result';
    }

    #requiredForStage(id) {
      switch (id) {
        case '1': return ['nicotine_form'];                  // dose has a default
        case '2': return ['carrier', 'sealing'];             // both must be picked
        case '3': return [];                                 // pH has a default
        case '4': return [];                                 // moisture has a default
        case '5': return [];                                 // sweetener / flavour optional
        default:  return [];
      }
    }

    // ── Per-step feedback ───────────────────────────────────
    #renderStep(field) {
      switch (field) {
        case 'nicotine_form':
        case 'dose_mg':       return this.#renderStep1();
        case 'carrier':
        case 'sealing':       return this.#renderStep2();
        case 'ph':            return this.#renderStep3();
        case 'moisture_pct':  return this.#renderStep4();
        case 'sweetener':
        case 'flavor':        return this.#renderStep5();
      }
    }

    #fb(stepKey) { return this.querySelector(`[data-zp-diy-feedback="${stepKey}"]`); }

    #renderStep1() {
      const out = this.#fb('step-1');
      const formKey = this.state.nicotine_form;
      if (!formKey) return;
      const form = FEEDBACK.step1.forms[formKey];
      const band = FEEDBACK.step1.doseBands.find((b) => this.state.dose_mg <= b.max);
      out.innerHTML = `
        ${this.#tag(form.tone)}
        <h4>${form.title}</h4>
        <p>${form.body}</p>
        ${band ? `<p><strong>${this.state.dose_mg} mg / pouch:</strong> ${band.text}</p>` : ''}
      `;
    }

    #renderStep2() {
      const out = this.#fb('step-2');
      const cKey = this.state.carrier, sKey = this.state.sealing;
      if (!cKey && !sKey) return;
      const parts = [];
      if (cKey) {
        const c = FEEDBACK.step2.carrier[cKey];
        parts.push(`${this.#tag(c.tone)}<h4>Carrier — ${c.title}</h4><p>${c.body}</p>`);
      }
      if (sKey) {
        const s = FEEDBACK.step2.sealing[sKey];
        parts.push(`${this.#tag(s.tone)}<h4>Seal — ${s.title}</h4><p>${s.body}</p>`);
      }
      out.innerHTML = parts.join('');
    }

    #renderStep3() {
      const out = this.#fb('step-3');
      const ph = this.state.ph / 10;
      const fb = freeBasePct(ph);
      let tone, title, body;

      if (ph < 6.0) {
        tone = 'bad';
        title = 'Too acidic — chemistry stops working';
        body = `At pH ${ph.toFixed(1)} only about ${fb.toFixed(1)}% of nicotine is in the absorbable free-base form. The product would feel inert, and prolonged acidic exposure can erode tooth enamel.`;
      } else if (ph < 6.5) {
        tone = 'warn';
        title = 'Below the commercial spectrum';
        body = `${fb.toFixed(1)}% free-base nicotine. Below where Stanfill et al. (2021) found any commercial pouch (their lowest was 6.86); absorption would feel very weak.`;
      } else if (ph <= 7.5) {
        tone = 'ok';
        title = 'Near-neutral design segment';
        body = `${fb.toFixed(1)}% of the nicotine is in the free-base form at pH ${ph.toFixed(1)}. A segment of commercial brands deliberately targets this near-neutral range for a smoother release and reduced gum irritation, at the cost of slower nicotine absorption.`;
      } else if (ph <= 9.0) {
        tone = 'ok';
        title = 'Mainstream commercial range';
        body = `${fb.toFixed(1)}% free-base at pH ${ph.toFixed(1)} — where most major brands measure in independent testing (Stanfill et al. 2021; Nature Sci Rep 2025).`;
      } else if (ph <= 9.5) {
        tone = 'ok';
        title = 'High-alkalinity segment';
        body = `${fb.toFixed(1)}% free-base nicotine. Higher pH means faster release; a smaller segment of commercial pouches sits here, though mucosal irritation reports increase as alkalinity climbs.`;
      } else if (ph <= 10) {
        tone = 'warn';
        title = 'Above the commercial range';
        body = `${fb.toFixed(1)}% free-base. Above where commercial pouches are characterised; mucosal irritation is consistently reported as alkalinity climbs above 9.5.`;
      } else {
        tone = 'bad';
        title = 'Caustic — chemical-burn risk';
        body = `pH ${ph.toFixed(1)} is strongly alkaline. Sustained contact at this pH can chemically burn the gum and the lining of the mouth.`;
      }
      out.innerHTML = `${this.#tag(tone)}<h4>${title}</h4><p>${body}</p>`;
    }

    #renderStep4() {
      const out = this.#fb('step-4');
      const m = this.state.moisture_pct;
      let tone, title, body;
      if (m < 20) {
        tone = 'warn'; title = 'Too dry to release nicotine effectively';
        body = 'Saliva alone has to rehydrate the pouch before any nicotine moves; the experience will feel weak and uneven.';
      } else if (m < 30) {
        tone = 'warn'; title = 'Below the typical commercial range';
        body = 'Drier than most retail pouches. Release will be slower and more variable.';
      } else if (m <= 50) {
        tone = 'ok'; title = 'Within the snus-style commercial band';
        body = 'Traditional Swedish snus is ~45–60 % water (Rutqvist 2011). Modern tobacco-free pouches vary far more widely — Stanfill et al. (2021) measured 1–47 % moisture across the US market — but snus-style products cluster in this band. <strong>Shelf life is still the problem:</strong> homemade pouches lack the nitrogen-flush and oxygen-barrier packaging used industrially, so nicotine oxidises and yellowing appears within roughly 7–10 days at room temperature, with proportional potency loss.';
      } else if (m <= 65) {
        tone = 'warn'; title = 'Above the commercial range';
        body = 'Higher moisture means faster release but a much shorter safe shelf life, especially without controlled packaging and water-activity testing.';
      } else {
        tone = 'bad'; title = 'High microbial-growth risk';
        body = 'Microbial spoilage is governed by water activity (a_w), not just % moisture. Without humectants and sealed packaging to keep a_w below ~0.7, a moist sweetened pouch can support mould and yeast growth at room temperature — commercial products use PG / glycerin specifically to bind that water.';
      }
      out.innerHTML = `${this.#tag(tone)}<h4>${title}</h4><p>${body}</p>`;
    }

    #renderStep5() {
      const out = this.#fb('step-5');
      const s = this.state.sweetener, f = this.state.flavor;
      if (!s && !f) return;
      const parts = [];
      if (s) {
        const item = FEEDBACK.step5.sweetener[s];
        parts.push(`${this.#tag(item.tone)}<p><strong>Sweetener — ${LABELS.sweetener[s]}:</strong> ${item.text}</p>`);
      }
      if (f) {
        const item = FEEDBACK.step5.flavor[f];
        parts.push(`${this.#tag(item.tone)}<p><strong>Flavour — ${LABELS.flavor[f]}:</strong> ${item.text}</p>`);
      }
      out.innerHTML = parts.join('');
    }

    #tag(tone) {
      return `<span class="zp-diy__tag zp-diy__tag--${tone === 'info' ? 'ok' : tone}">${TONES[tone] || tone}</span>`;
    }

    // ── Scoring ─────────────────────────────────────────────
    #score() {
      const s = this.state;

      let dose = 30;
      if (s.nicotine_form === 'salt')     dose += 15;
      if (s.nicotine_form === 'freebase') dose -= 10;
      if (s.nicotine_form === 'nrt')      dose -= 5;
      if (s.nicotine_form === 'tobacco')  dose  = 25;
      if (s.dose_mg > 12) dose -= 10;
      if (s.dose_mg > 20) dose -= 15;
      dose = this.#clamp(dose);

      let cont = 45;
      const carrierScores = { cotton: 20, tissue: 15, tea: 25, mcc: 55, wood_pulp: 50 };
      if (s.carrier) cont = carrierScores[s.carrier];
      if (s.moisture_pct >= 30 && s.moisture_pct <= 50) cont += 10;
      if (s.moisture_pct > 55) cont -= 15;
      if (s.moisture_pct > 65) cont -= 15;
      if (s.sweetener === 'sugar')  cont -= 20;
      if (s.flavor    === 'extract') cont -= 5;
      // Sealing penalty: bad seal = leak path for contaminants + filler escape
      const sealCont = { open: -20, glue: -15, staple: -10, sewing: -10, iron: -10, foodsaver: -10, impulse: 0 };
      if (s.sealing && sealCont[s.sealing] != null) cont += sealCont[s.sealing];
      cont = this.#clamp(cont);

      let muc = 65;
      const ph = s.ph / 10;
      // Full commercial spectrum (near-neutral, mainstream, high-alkalinity)
      // gets full credit. Only out-of-spectrum pH penalises mucosal safety.
      if (ph >= 6.5 && ph <= 9.5) muc += 20;
      else if (ph >= 6.0 && ph < 6.5) muc -= 10;
      else if (ph > 9.5 && ph <= 10)  muc -= 15;
      else muc -= 45;
      if (s.flavor === 'essential_oil') muc -= 20;
      if (s.nicotine_form === 'freebase' && s.dose_mg >= 10) muc -= 10;
      if (s.carrier === 'tea') muc -= 5;
      // Sealing penalty: leaked nicotine concentrate is mucosal-burn / dose-spike risk
      const sealMuc = { open: -20, glue: -15, staple: -10, sewing: -5, iron: 0, foodsaver: 0, impulse: 0 };
      if (s.sealing && sealMuc[s.sealing] != null) muc += sealMuc[s.sealing];
      muc = this.#clamp(muc);

      let reg = 5;
      if (s.nicotine_form === 'nrt') reg = 8;
      reg = this.#clamp(reg);

      return { dose, contamination: cont, mucosa: muc, regulatory: reg };
    }

    #toneFor(v) {
      if (v >= 70) return 'ok';
      if (v >= 40) return 'warn';
      return 'bad';
    }

    #clamp(v) { return Math.max(0, Math.min(100, Math.round(v))); }

    #verdict(scores) {
      const avg = (scores.dose + scores.contamination + scores.mucosa + scores.regulatory) / 4;
      if (avg < 25) return 'This build would not be safe to put in your mouth, and it could not be sold legally. Use it as a reminder of what regulated manufacturing is for.';
      if (avg < 45) return 'Even with one or two industry-aligned choices, dosing precision and regulatory standing make this hard to recommend.';
      if (avg < 65) return 'The ingredient picks are closer to industry norms, but the dosing-accuracy and regulatory gaps remain unresolvable at home.';
      return 'A best-case homemade attempt still cannot meet the dosing tolerance, microbial testing, and regulatory filing that any retail-grade pouch is held to.';
    }

    /**
     * Letter grade from the four scores. Educational caps mean the regulatory dimension
     * never exceeds ~10, so even a chemistry-perfect build tops out around C — that is the
     * pedagogical point.
     */
    #grade(scores) {
      const overall = Math.round((scores.dose + scores.contamination + scores.mucosa + scores.regulatory) / 4);
      if (overall >= 75) return { letter: 'A', tone: 'ok',   tier: 'Closer than most — but still not legal to sell.', overall };
      if (overall >= 60) return { letter: 'B', tone: 'ok',   tier: 'Defensible chemistry, fatal regulatory problem.', overall };
      if (overall >= 45) return { letter: 'C', tone: 'warn', tier: 'About as far as a homemade build can realistically go — dosing precision and regulatory authorisation are the two ceilings home production cannot cross.', overall };
      if (overall >= 25) return { letter: 'D', tone: 'bad',  tier: 'Don\'t put this in your mouth.',                  overall };
      return                       { letter: 'F', tone: 'bad',  tier: 'Just don\'t. Multiple acute safety issues.',      overall };
    }

    #emojiBar(score, tone) {
      const SQUARE = { ok: '🟩', warn: '🟧', bad: '🟥' };
      const seg = Math.round(score / 10);
      return SQUARE[tone].repeat(seg) + '⬜'.repeat(10 - seg);
    }

    // ── Reveal ──────────────────────────────────────────────
    #reveal() {
      const scores = this.#score();
      this._scores = scores;
      this.#paintShareCard(scores);
      this.#goto(STAGES.indexOf('result'));
    }

    #reset() {
      this.querySelectorAll('input[type="radio"]').forEach((el) => (el.checked = false));
      this.state = { nicotine_form: null, dose_mg: 6, carrier: null, sealing: null, ph: 70, moisture_pct: 35, sweetener: null, flavor: null };
      ['dose_mg', 'ph', 'moisture_pct'].forEach((f) => {
        const slider = this.querySelector(`input[data-zp-diy-field="${f}"]`);
        if (slider) slider.value = this.state[f];
        this.#syncDisplay(f);
      });
      this.querySelectorAll('[data-zp-diy-feedback]').forEach((el) => {
        const empty = {
          'step-1': 'Pick a starting material to see its safety profile.',
          'step-2': 'Pick a carrier and a sealing method.',
          'step-3': 'Move the slider to see absorption vs. mucosal-irritation effects.',
          'step-4': 'Adjust to balance release rate against microbial stability.',
          'step-5': 'Anything in here ends up against the mucosa for 20–60 minutes.',
        };
        const key = el.dataset.zpDiyFeedback;
        el.innerHTML = `<p class="zp-diy__fb-empty">${empty[key] || 'Cleared.'}</p>`;
      });
      this._scores = null;
      this.#goto(0);
    }

    // ── Share card paint ────────────────────────────────────
    #paintShareCard(scores) {
      const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      this.querySelector('[data-zp-diy-share-date]').textContent = date;

      const grade = this.#grade(scores);
      const gradeNode = this.querySelector('[data-zp-diy-share-grade]');
      gradeNode.textContent = grade.letter;
      gradeNode.dataset.gradeTone = grade.tone;
      this.querySelector('[data-zp-diy-share-overall]').textContent = String(grade.overall);
      this.querySelector('[data-zp-diy-share-tier]').textContent = grade.tier;

      const ph = (this.state.ph / 10).toFixed(1);
      const spec = [
        this.state.nicotine_form ? `${LABELS.nicotine_form[this.state.nicotine_form]} · ${this.state.dose_mg}mg` : null,
        this.state.carrier ? LABELS.carrier[this.state.carrier] : null,
        this.state.sealing && this.state.sealing !== 'impulse' ? `seal: ${LABELS.sealing[this.state.sealing]}` : null,
        `pH ${ph}`,
        `${this.state.moisture_pct}% moist`,
        this.state.sweetener && this.state.sweetener !== 'none' ? LABELS.sweetener[this.state.sweetener] : null,
        this.state.flavor && this.state.flavor !== 'none' ? LABELS.flavor[this.state.flavor] : null,
      ].filter(Boolean);
      this.querySelector('[data-zp-diy-share-spec]').innerHTML = spec.map((t) => `<li>${t}</li>`).join('');

      Object.entries(scores).forEach(([k, v]) => {
        const valEl = this.querySelector(`[data-zp-diy-share-score="${k}"]`);
        const barEl = this.querySelector(`[data-zp-diy-share-bar="${k}"]`);
        if (valEl) valEl.textContent = String(v);
        if (!barEl) return;
        const tone = this.#toneFor(v);
        barEl.dataset.tone = tone;
        const seg = Math.round(v / 10);
        barEl.innerHTML = Array.from({ length: 10 },
          (_, i) => `<span${i < seg ? ' class="is-on"' : ''}></span>`).join('');
      });
    }

    // ── Share actions ───────────────────────────────────────
    #bindShareActions() {
      this.querySelector('[data-zp-diy-share-copy]')?.addEventListener('click', () => this.#copySummary());
      this.querySelector('[data-zp-diy-share-download]')?.addEventListener('click', () => this.#downloadPng());
    }

    #copySummary() {
      if (!this._scores) return;
      const s = this.state, sc = this._scores;
      const ph = (s.ph / 10).toFixed(1);
      const grade = this.#grade(sc);
      const row = (label, score) => {
        const tone = this.#toneFor(score);
        const bar = this.#emojiBar(score, tone);
        return `${bar}  ${label.padEnd(14)} ${String(score).padStart(3)}`;
      };
      const spec = [
        `${LABELS.nicotine_form[s.nicotine_form] || '—'} · ${s.dose_mg}mg`,
        LABELS.carrier[s.carrier] || '—',
      ];
      if (s.sealing && s.sealing !== 'impulse') spec.push(`seal: ${LABELS.sealing[s.sealing]}`);
      spec.push(`pH ${ph}`, `${s.moisture_pct}% moist`);
      if (s.sweetener && s.sweetener !== 'none') spec.push(LABELS.sweetener[s.sweetener]);
      if (s.flavor    && s.flavor    !== 'none') spec.push(LABELS.flavor[s.flavor]);

      const lines = [
        `My Homemade Nicotine Pouch — Grade ${grade.letter} (${grade.overall}/100)`,
        `"${grade.tier}"`,
        '',
        row('Dose',          sc.dose),
        row('Contamination', sc.contamination),
        row('Mucosa',        sc.mucosa),
        row('Regulatory',    sc.regulatory),
        '',
        `Build: ${spec.join(' · ')}`,
        '',
        'zarpouch.com/pages/homemade-nicotine-pouches',
      ].join('\n');

      const done = (ok) => {
        const lbl = this.querySelector('[data-zp-diy-share-copy-label]');
        if (!lbl) return;
        const prev = lbl.textContent;
        lbl.textContent = ok ? 'Copied ✓' : 'Copy failed';
        setTimeout(() => (lbl.textContent = prev), 1800);
      };

      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(lines).then(() => done(true), () => done(false));
      } else {
        try {
          const ta = document.createElement('textarea');
          ta.value = lines; document.body.appendChild(ta);
          ta.select(); document.execCommand('copy'); ta.remove();
          done(true);
        } catch { done(false); }
      }
    }

    // ── PNG download (Canvas; no external deps) ─────────────
    #downloadPng() {
      if (!this._scores) return;

      const SIZE = 1080;
      const canvas = document.createElement('canvas');
      canvas.width = SIZE; canvas.height = SIZE;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Palette — matches the dark-navy social card
      const INK    = '#f7f5ef';
      const MUTED  = 'rgba(247, 245, 239, 0.55)';
      const LINE   = 'rgba(247, 245, 239, 0.18)';
      const FILL   = 'rgba(247, 245, 239, 0.10)';
      const ACCENT = '#22d3ee';
      const TONE_COLOR = { ok: '#34d399', warn: '#fbbf24', bad: '#f87171' };
      const family = 'system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif';
      const mono   = '"SF Mono", ui-monospace, "JetBrains Mono", "Roboto Mono", Menlo, Consolas, monospace';
      const PAD    = 72;

      // Background — radial navy gradient + faint grid
      const bg = ctx.createRadialGradient(SIZE * 0.0, 0, 0, SIZE * 0.0, 0, SIZE * 1.3);
      bg.addColorStop(0, '#15323b');
      bg.addColorStop(0.55, '#0e2128');
      bg.addColorStop(1, '#08151a');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, SIZE, SIZE);

      // Grid
      ctx.strokeStyle = 'rgba(255,255,255,0.035)';
      ctx.lineWidth = 1;
      for (let i = 60; i < SIZE; i += 60) {
        ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, SIZE); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(SIZE, i); ctx.stroke();
      }

      // Top bar — brand mark + date
      ctx.textBaseline = 'top';
      ctx.fillStyle = ACCENT;
      ctx.font = `600 22px ${mono}`;
      ctx.fillText('◆ HOMEMADE NICOTINE POUCH', PAD, PAD);

      const dateText = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      ctx.fillStyle = MUTED;
      ctx.font = `400 22px ${mono}`;
      const dateW = ctx.measureText(dateText).width;
      ctx.fillText(dateText, SIZE - PAD - dateW, PAD);

      // Hero — big grade letter + overall score
      const grade = this.#grade(this._scores);
      const gradeColor = TONE_COLOR[grade.tone];
      const heroY = PAD + 72;
      const boxSize = 200;

      ctx.fillStyle = `${gradeColor}14`;
      this.#roundRect(ctx, PAD, heroY, boxSize, boxSize, 24);
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = gradeColor;
      this.#roundRect(ctx, PAD, heroY, boxSize, boxSize, 24);
      ctx.stroke();

      ctx.fillStyle = gradeColor;
      ctx.font = `800 160px ${family}`;
      ctx.textBaseline = 'middle';
      const letterW = ctx.measureText(grade.letter).width;
      ctx.fillText(grade.letter, PAD + (boxSize - letterW) / 2, heroY + boxSize / 2 + 6);

      // Overall number next to the grade
      ctx.textBaseline = 'alphabetic';
      ctx.fillStyle = INK;
      ctx.font = `700 110px ${mono}`;
      const overallStr = String(grade.overall);
      const overallW = ctx.measureText(overallStr).width;  // measure WHILE font is still 110px
      ctx.fillText(overallStr, PAD + boxSize + 40, heroY + 130);
      ctx.fillStyle = MUTED;
      ctx.font = `400 26px ${mono}`;
      ctx.fillText('/ 100', PAD + boxSize + 40 + overallW + 14, heroY + 130);

      // Tier label below hero
      ctx.fillStyle = INK;
      ctx.font = `600 30px ${family}`;
      ctx.textBaseline = 'top';
      const tierY = heroY + boxSize + 40;
      const tierLineH = 38;
      const tierLastLineTop = this.#wrapText(ctx, grade.tier, PAD, tierY, SIZE - PAD * 2, tierLineH);
      const tierBottom = tierLastLineTop + tierLineH;

      // 10-segment bars — start at least 110px below tier start (short tier) OR
      // 36px below the actual end of the wrapped tier text (long tier).
      const rows = [
        ['DOSE PRECISION',  'dose'],
        ['CONTAMINATION',   'contamination'],
        ['MUCOSAL SAFETY',  'mucosa'],
        ['REGULATORY',      'regulatory'],
      ];
      const barsY = Math.max(tierY + 110, tierBottom + 36);
      const rowGap = 56;
      const labelW = 320, valW = 80;
      const barLeft  = PAD + labelW + 20;
      const barRight = SIZE - PAD - valW - 20;
      const barWidth = barRight - barLeft;
      const segGap = 6;
      const segW = (barWidth - segGap * 9) / 10;
      const segH = 22;

      rows.forEach(([label, key], i) => {
        const y = barsY + i * rowGap;
        // Label
        ctx.fillStyle = MUTED;
        ctx.font = `500 18px ${mono}`;
        ctx.textBaseline = 'middle';
        ctx.fillText(label, PAD, y + segH / 2);

        // Segments
        const score = this._scores[key];
        const tone = this.#toneFor(score);
        const filled = Math.round(score / 10);
        for (let s = 0; s < 10; s++) {
          const sx = barLeft + s * (segW + segGap);
          this.#roundRect(ctx, sx, y, segW, segH, 4);
          ctx.fillStyle = s < filled ? TONE_COLOR[tone] : FILL;
          ctx.fill();
        }

        // Value
        ctx.fillStyle = INK;
        ctx.font = `700 26px ${mono}`;
        const valStr = String(score);
        const vw = ctx.measureText(valStr).width;
        ctx.fillText(valStr, SIZE - PAD - vw, y + segH / 2 + 2);
      });

      // Spec strip
      const specY = barsY + rows.length * rowGap + 40;
      const ph = (this.state.ph / 10).toFixed(1);
      const spec = [
        `${LABELS.nicotine_form[this.state.nicotine_form]} · ${this.state.dose_mg}mg`,
        LABELS.carrier[this.state.carrier],
        this.state.sealing && this.state.sealing !== 'impulse' ? `seal: ${LABELS.sealing[this.state.sealing]}` : null,
        `pH ${ph}`,
        `${this.state.moisture_pct}% moist`,
        this.state.sweetener && this.state.sweetener !== 'none' ? LABELS.sweetener[this.state.sweetener] : null,
        this.state.flavor    && this.state.flavor    !== 'none' ? LABELS.flavor[this.state.flavor]    : null,
      ].filter(Boolean);

      ctx.font = `400 19px ${mono}`;
      const SEP = '   ·   ';
      const innerW = SIZE - PAD * 2 - 40;
      // Pack specs onto up to 2 lines
      const specLines = [];
      let line = '';
      for (const item of spec) {
        const candidate = line ? line + SEP + item : item;
        if (ctx.measureText(candidate).width > innerW && line) {
          specLines.push(line);
          line = item;
          if (specLines.length >= 1) {
            // We're filling the second line; if a later item still won't fit,
            // it will be dropped at the truncate step below.
          }
        } else {
          line = candidate;
        }
      }
      if (line) specLines.push(line);
      const linesToShow = specLines.slice(0, 2);
      const specH = linesToShow.length === 1 ? 60 : 84;

      ctx.fillStyle = 'rgba(255,255,255,0.04)';
      this.#roundRect(ctx, PAD, specY, SIZE - PAD * 2, specH, 12);
      ctx.fill();
      ctx.strokeStyle = LINE; ctx.lineWidth = 1;
      this.#roundRect(ctx, PAD, specY, SIZE - PAD * 2, specH, 12);
      ctx.stroke();

      ctx.textBaseline = 'middle';
      ctx.fillStyle = MUTED;
      const lineH = 28;
      const blockH = linesToShow.length * lineH;
      const startY = specY + (specH - blockH) / 2 + lineH / 2;
      linesToShow.forEach((l, i) => ctx.fillText(l, PAD + 20, startY + i * lineH));

      // Footer — two lines, both centered (Spotify-Wrapped style)
      const urlY     = SIZE - PAD - 6;
      const hookY    = urlY - 36;
      const dividerY = hookY - 24;

      // Thin divider, narrower than full width for a more "card" feel
      const dividerW = 240;
      ctx.strokeStyle = LINE; ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo((SIZE - dividerW) / 2, dividerY);
      ctx.lineTo((SIZE + dividerW) / 2, dividerY);
      ctx.stroke();

      ctx.textBaseline = 'alphabetic';

      // Line 1 — italic hook, centered
      ctx.fillStyle = MUTED;
      ctx.font = `italic 400 22px ${family}`;
      ctx.textAlign = 'center';
      ctx.fillText('I tried building a nicotine pouch at home.', SIZE / 2, hookY);

      // Line 2 — URL centered (host + slug). Measure both, then left-draw from centered start.
      const url  = 'zarpouch.com';
      const slug = '/pages/homemade-nicotine-pouches';
      ctx.textAlign = 'left';
      ctx.font = `600 22px ${mono}`;
      const urlW  = ctx.measureText(url).width;
      ctx.font = `400 20px ${mono}`;
      const slugW = ctx.measureText(slug).width;
      const gap = 6;
      const totalW = urlW + gap + slugW;
      const xStart = (SIZE - totalW) / 2;

      ctx.fillStyle = ACCENT;
      ctx.font = `600 22px ${mono}`;
      ctx.fillText(url, xStart, urlY);
      ctx.fillStyle = MUTED;
      ctx.font = `400 20px ${mono}`;
      ctx.fillText(slug, xStart + urlW + gap, urlY);

      ctx.textAlign = 'left'; // restore for safety

      canvas.toBlob((blob) => {
        if (!blob) return;
        const url2 = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url2;
        a.download = `homemade-nicotine-pouch-grade-${this.#grade(this._scores).letter}-${Date.now()}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url2);
      }, 'image/png');
    }

    #roundRect(ctx, x, y, w, h, r) {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.arcTo(x + w, y, x + w, y + h, r);
      ctx.arcTo(x + w, y + h, x, y + h, r);
      ctx.arcTo(x, y + h, x, y, r);
      ctx.arcTo(x, y, x + w, y, r);
      ctx.closePath();
    }

    #wrapText(ctx, text, x, y, maxW, lineH) {
      const words = text.split(' ');
      let line = '';
      for (let i = 0; i < words.length; i++) {
        const test = line ? `${line} ${words[i]}` : words[i];
        if (ctx.measureText(test).width > maxW && line) {
          ctx.fillText(line, x, y);
          y += lineH;
          line = words[i];
        } else {
          line = test;
        }
      }
      if (line) ctx.fillText(line, x, y);
      return y;
    }
  }

  if (!customElements.get('zp-diy-simulator')) {
    customElements.define('zp-diy-simulator', ZpDiySimulator);
  }
})();
